<template>
  <v-container fluid class="pa-4">
    <!-- <PageHeader
      icon="mdi-cog"
      title="Konfigurasi"
      subtitle="Tetapan sistem yang boleh diubah tanpa membina semula aplikasi"
    /> -->

    <v-card outlined>
      <v-card-title class="py-3 kad__tajuk d-flex align-center">
        <v-icon small class="mr-2">mdi-cog</v-icon>
        <span class="subtitle-1 font-weight-medium">Konfigurasi Sistem</span>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-badge
              :value="missingCount > 0"
              :content="missingCount"
              color="orange darken-3"
              overlap
              class="mr-2"
            >
              <v-btn
                outlined
                class="reset-btn text-none"
                :loading="seeding"
                v-bind="attrs"
                v-on="on"
                @click="seedDefaults"
              >
                <v-icon left>mdi-database-sync</v-icon> Selaras Default
              </v-btn>
            </v-badge>
          </template>
          <span>{{ seedTooltip }}</span>
        </v-tooltip>
      </v-card-title>
      <v-card-text class="pt-4">

        <!-- Prompt: registry has defaults the DB doesn't. Seeding is done here,
             not by re-running schema/config-table.sql. -->
        <v-alert
          v-if="missingCount > 0"
          type="info"
          text
          dense
          border="left"
          class="mb-4"
        >
          <div class="d-flex align-center flex-wrap">
            <span>
              <strong>{{ missingCount }}</strong> konfigurasi default baharu belum wujud
              dalam pangkalan data ({{ missingNames }}).
            </span>
            <v-spacer></v-spacer>
            <v-btn small class="btn-teal text-none" :loading="seeding" @click="seedDefaults">
              <v-icon left small>mdi-database-sync</v-icon> Selaras Default
            </v-btn>
          </div>
        </v-alert>

        <!-- Bar penapis: jalur teal + label kening di atas medan, susunan yang
             sama seperti Jadual Awalan / Julat Harga / Log Audit. Medan
             disaiz mengikut kandungan — beberapa nilai type yang pendek tidak
             memerlukan select separuh halaman. -->
        <div class="filter-bar mb-4">
          <v-row dense align="end">
            <v-col cols="12" sm="4" md="3">
              <div class="fld__label">Tapis Type</div>
              <v-select
                v-model="selectedType"
                :items="types"
                prepend-inner-icon="mdi-filter-variant"
                placeholder="Semua" persistent-placeholder
                dense
                outlined
                clearable
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="5" md="4">
              <div class="fld__label">Carian</div>
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Cari nama atau nilai" persistent-placeholder
                dense
                outlined
                clearable
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredConfigs"
          :search="search"
          :loading="loading"
          :items-per-page="-1"
          hide-default-footer
          class="elevation-0 cfg-table"
        >
          <template v-slot:[`item.description`]="{ item }">
            <span class="config-desc text--secondary" v-html="descHtml(item.description)"></span>
          </template>
          <template v-slot:[`item.value`]="{ item }">
            <!-- Colour rows show the colour itself — a hex string alone is
                 unreadable when you are scanning the list for the one to edit. -->
            <span v-if="isColourRow(item)" class="cfg-swatch mr-2" :style="{ background: item.value }"></span>
            <span>{{ item.value }}</span>
            <div
              v-if="item.default_value != null && String(item.value) !== String(item.default_value)"
              class="text-caption grey--text"
            >
              default: {{ item.default_value || '(kosong)' }}
            </div>
          </template>
          <template v-slot:[`item.last_updated_at`]="{ item }">
            {{ formatDate(item.last_updated_at) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon small class="mr-2" color="#037769" @click="openDialog(item)">mdi-pencil</v-icon>
            <v-icon small color="#d9534f" @click="confirmDelete(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog Edit — edit only. Rows come from the code registry
         (configDefaults.js) via "Selaras Default", never hand-added: an
         arbitrary type/name would be a row nothing reads. -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-pencil</v-icon>
          <span class="subtitle-1 font-weight-medium">Kemaskini Konfigurasi</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field v-model="editedItem.type" label="Type" disabled></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editedItem.name" label="Name" disabled></v-text-field>
              </v-col>
              <v-col cols="12" v-if="editedItem.description">
                <v-alert type="info" dense text class="mb-0 caption">
                  <span v-html="descHtml(editedItem.description)"></span>
                </v-alert>
              </v-col>
              <!-- Colour rows get the OS colour picker beside the field. The
                   text field stays editable so an exact hex can still be pasted
                   in — the picker is the convenience, not the only way in. -->
              <v-col cols="12" class="d-flex align-center">
                <input
                  v-if="isColour"
                  type="color"
                  class="cfg-picker mr-3"
                  :value="pickerValue"
                  :aria-label="`Pilih warna untuk ${editedItem.name}`"
                  @input="editedItem.value = $event.target.value.toUpperCase()"
                >
                <v-text-field
                  v-model="editedItem.value"
                  label="Value"
                  :hint="isColour ? 'Format #RRGGBB' : ''"
                  :persistent-hint="isColour"
                  class="flex-grow-1"
                ></v-text-field>
              </v-col>
              <v-col v-if="hasDefault" cols="12" class="pt-0">
                <div class="d-flex align-center text-caption grey--text">
                  <span>Nilai default: <strong>{{ editedItem.default_value || '(kosong)' }}</strong></span>
                  <v-btn
                    v-if="!isDefaultValue"
                    x-small
                    text
                    color="#037769"
                    class="ml-2"
                    @click="revertToDefault"
                  >
                    <v-icon x-small left>mdi-restore</v-icon> Guna Default
                  </v-btn>
                  <v-chip v-else x-small color="green lighten-4" class="ml-2">default</v-chip>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <v-alert v-if="errorMsg" type="error" dense>{{ errorMsg }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="closeDialog">Batal</v-btn>
          <v-btn class="btn-teal text-none" @click="save" :loading="saving">Simpan</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Delete -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-delete-alert</v-icon>
          <span class="subtitle-1 font-weight-medium">Adakah anda pasti untuk memadam?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="deleteDialog = false">Batal</v-btn>
          <v-btn color="red darken-1" text @click="deleteItemConfirm" :loading="deleting">Padam</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from '@/plugins/axios'
import { reloadTheme } from '@/services/theme'

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export default {
  name: 'ConfigManager',
  data: () => ({
    search: '',
    selectedType: null,
    loading: false,
    dialog: false,
    deleteDialog: false,
    headers: [
      { text: 'Type', value: 'type' },
      { text: 'Name', value: 'name' },
      { text: 'Value', value: 'value' },
      { text: 'Panduan', value: 'description', sortable: false },
      { text: 'Dikemaskini Oleh', value: 'last_updated_by_name' },
      { text: 'Tarikh Kemaskini', value: 'last_updated_at' },
      { text: 'Tindakan', value: 'actions', sortable: false }
    ],
    configs: [],
    // Registry defaults not yet in the DB (from GET /admin/config).
    missingDefaults: [],
    editedItem: {
      id: null,
      type: '',
      name: '',
      value: '',
      default_value: null
    },
    defaultItem: {
      id: null,
      type: '',
      name: '',
      value: '',
      default_value: null
    },
    seeding: false,
    saving: false,
    deleting: false,
    itemToDelete: null,
    errorMsg: '',
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success'
  }),
  computed: {
    apiUrl() {
      return process.env.VUE_APP_API_URL
    },
    types() {
      const allTypes = this.configs.map(c => c.type)
      return [...new Set(allTypes)].sort()
    },
    filteredConfigs() {
      if (!this.selectedType) return this.configs
      return this.configs.filter(c => c.type === this.selectedType)
    },
    missingCount() {
      return this.missingDefaults.length
    },
    missingNames() {
      return this.missingDefaults.map(d => `${d.type}/${d.name}`).join(', ')
    },
    seedTooltip() {
      if (!this.missingCount) return 'Semua konfigurasi default sudah wujud.'
      return `${this.missingCount} konfigurasi default belum wujud: ${this.missingNames}`
    },
    // Whether the row being edited has a known default value.
    hasDefault() {
      return this.editedItem.default_value !== null && this.editedItem.default_value !== undefined
    },
    // Whether the current value already equals the default.
    isDefaultValue() {
      return String(this.editedItem.value) === String(this.editedItem.default_value)
    },
    isColour() {
      return this.isColourRow(this.editedItem)
    },
    // <input type="color"> only accepts a full #rrggbb; anything else (a
    // half-typed hex, a cleared field) would make it silently show black and
    // then write that black back on the next click. Fall back to the row's
    // default so the picker opens on a sane colour instead.
    pickerValue() {
      const v = String(this.editedItem.value || '').trim()
      if (HEX_RE.test(v)) return v
      const d = String(this.editedItem.default_value || '').trim()
      return HEX_RE.test(d) ? d : '#000000'
    }
  },
  created() {
    this.fetchConfigs()
  },
  methods: {
    // A row is a colour when its DEFAULT is a hex — derived from the row itself
    // rather than a hardcoded list of names, so a colour added to the backend
    // registry later gets the picker without touching this file. Falls back to
    // the current value for a row whose default is missing.
    isColourRow(item) {
      if (!item) return false
      return HEX_RE.test(String(item.default_value || '').trim()) ||
             HEX_RE.test(String(item.value || '').trim())
    },
    // Panduan supports **teks penting** markers → <b> and newlines → <br>.
    // The text is DB content, so escape ALL HTML first and only then translate
    // the markers — keeps the v-html rendering safe from anything else stored
    // in the column.
    descHtml(text) {
      if (!text) return '—';
      const esc = String(text).replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
      return esc.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>').replace(/\r?\n/g, '<br>');
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return d.toLocaleString('en-GB')
    },
    showSnack(msg, color = 'success') {
      this.snackbarText = msg
      this.snackbarColor = color
      this.snackbar = true
    },
    async fetchConfigs() {
      this.loading = true
      try {
        const res = await axios.get(`${this.apiUrl}/admin/config`)
        this.configs = res.data.config || []
        this.missingDefaults = res.data.missingDefaults || []
        // Re-read the live palette from the same source of truth. Every write
        // path refetches, so this one call keeps the app's colours in step with
        // the table you are looking at — no browser reload needed.
        reloadTheme()
      } catch (err) {
        console.error(err)
        this.showSnack('Ralat memuat turun konfigurasi', 'error')
      } finally {
        this.loading = false
      }
    },
    async seedDefaults() {
      this.seeding = true
      try {
        const res = await axios.post(`${this.apiUrl}/admin/config/seed`)
        const n = res.data.insertedCount || 0
        // Always refetch: descriptions are refreshed even when nothing is
        // inserted, and this clears the "belum wujud" prompt.
        await this.fetchConfigs()
        this.showSnack(
          n > 0 ? `${n} konfigurasi default ditambah.` : 'Semua konfigurasi default sudah wujud.',
          n > 0 ? 'success' : 'info'
        )
      } catch (err) {
        console.error(err)
        this.showSnack('Ralat menyelaras konfigurasi default', 'error')
      } finally {
        this.seeding = false
      }
    },
    revertToDefault() {
      this.editedItem.value = this.editedItem.default_value
    },
    openDialog(item) {
      this.errorMsg = ''
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
      })
    },
    // Update only — `value` is the sole editable field (type/name identify the
    // row and are owned by the code registry).
    async save() {
      this.errorMsg = ''
      this.saving = true
      try {
        await axios.put(`${this.apiUrl}/admin/config/${this.editedItem.id}`, {
          value: this.editedItem.value
        })
        this.closeDialog()
        // Refetch instead of patching the row locally: re-runs the Selaras Default
        // check (missingDefaults) and pulls fresh default_value + audit columns.
        await this.fetchConfigs()
        this.showSnack('Konfigurasi berjaya dikemaskini')
      } catch (err) {
        console.error(err)
        this.errorMsg = err.response?.data?.message || 'Ralat menyimpan konfigurasi'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    async deleteItemConfirm() {
      this.deleting = true
      try {
        await axios.delete(`${this.apiUrl}/admin/config/${this.itemToDelete.id}`)
        this.deleteDialog = false
        // Refetch so the Selaras Default prompt re-appears when the deleted row was
        // a known default — it counts as "missing" again and can be restored.
        await this.fetchConfigs()
        this.showSnack('Konfigurasi berjaya dipadam')
      } catch (err) {
        console.error(err)
        this.showSnack('Ralat memadam konfigurasi', 'error')
      } finally {
        this.deleting = false
        this.itemToDelete = null
      }
    }
  }
}
</script>

<style scoped>
/* Kepala kad — corak yang sama seperti halaman lain: jalur putih dengan ikon
   dan tajuk, bukan blok warna. Dahulu jalur biru `.bg-primary` dengan kelas
   `headline` (24px), satu-satunya kepala biru dalam sistem teal. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon {
  color: #037769;
}

/* ---------- Bar penapis ----------
   Disalin daripada Jadual Awalan / Log Audit supaya jalur teal pucat menjadi
   isyarat "kawasan penapis" yang sama di seluruh sistem. */
.filter-bar {
  background: #f7fcfb;
  border: 1px solid #dbeeeb;
  border-radius: 4px;
  padding: 10px 12px 12px;
}
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.filter-bar ::v-deep .v-input fieldset { border-color: #cfeae7; }
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-input__prepend-inner .v-icon,
.filter-bar ::v-deep .v-input__append-inner .v-icon { color: #7aa8a2; }

/* Butang teal — menggantikan `color="primary"` (biru Vuetify). Warna literal
   kerana --teal-* ditakrif pada `.Menu`, bukan pada :root. */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}

/* Baris jadual sedikit lebih lega — lajur Panduan membalut kepada beberapa
   baris, jadi padding lalai membuatkan teks melekat pada garis grid. */
.cfg-table ::v-deep tbody td { padding-top: 10px; padding-bottom: 10px; }

/* ---------- Warna ----------
   Read-only chip in the Value column. */
.cfg-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, .2);
  vertical-align: -2px;
}

/* The native picker renders as a bordered button by default and its swatch has
   a few px of chrome padding — stripped so it reads as a plain colour tile next
   to the field. Both vendor prefixes are needed: Chromium uses
   ::-webkit-color-swatch, Firefox ::-moz-color-swatch. */
.cfg-picker {
  width: 44px;
  height: 34px;
  padding: 0;
  border: 1px solid #cfeae7;
  border-radius: 4px;
  background: none;
  cursor: pointer;
}
.cfg-picker::-webkit-color-swatch-wrapper { padding: 2px; }
.cfg-picker::-webkit-color-swatch { border: none; border-radius: 2px; }
.cfg-picker::-moz-color-swatch { border: none; border-radius: 2px; }

/* keep the guidance column readable: wrap long text into a fixed-width block
   instead of stretching the row across the page */
.config-desc {
  display: inline-block;
  max-width: 340px;
  white-space: normal;
  line-height: 1.35;
  font-size: var(--fs-xs);
}
</style>
